import { useState } from "react";
import { useAuth } from "@/hooks/useAuth";
import { showAlert } from '@/components/AppDialog';
import { useNavigate } from "react-router-dom";
import { useTranslation } from 'react-i18next';
import Navbar from "@/component/navbar";
import { API_BASE_URL } from "@/utils/env";
import { getCountries } from 'react-phone-number-input/input';

// A few markets to float around the globe as connectivity chips.
const CHIPS = [
  { code: "ae", label: "UAE",       cls: "cs-chip-a" },
  { code: "gb", label: "UK",        cls: "cs-chip-b" },
  { code: "us", label: "USA",       cls: "cs-chip-c" },
  { code: "sg", label: "Singapore", cls: "cs-chip-d" },
  { code: "in", label: "India",     cls: "cs-chip-e" },
];

// Animated SVG globe — a rotating orbit ring, an orbiting satellite dot,
// static meridians, pulsing location pins and a network arc between them.
// All motion is CSS keyframes (scoped, prefers-reduced-motion aware).
function AnimatedGlobe() {
  return (
    <svg viewBox="0 0 400 400" className="cs-globe w-full max-w-[440px]" role="img" aria-label="Global reach illustration">
      <defs>
        <radialGradient id="csGlobeFill" cx="38%" cy="32%" r="80%">
          <stop offset="0%" stopColor="#5AA0F0" />
          <stop offset="55%" stopColor="#1E74D4" />
          <stop offset="100%" stopColor="#0B4CA0" />
        </radialGradient>
        <linearGradient id="csRing" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%" stopColor="#BFDBFE" stopOpacity="0.9" />
          <stop offset="100%" stopColor="#60A5FA" stopOpacity="0.15" />
        </linearGradient>
        <filter id="csSoft" x="-40%" y="-40%" width="180%" height="180%">
          <feGaussianBlur stdDeviation="6" />
        </filter>
      </defs>

      {/* soft glow behind the globe */}
      <circle cx="200" cy="205" r="128" fill="#3B82F6" opacity="0.28" filter="url(#csSoft)" />

      {/* rotating orbit rings */}
      <g className="cs-spin" style={{ transformOrigin: "200px 200px" }}>
        <circle cx="200" cy="200" r="172" fill="none" stroke="url(#csRing)" strokeWidth="1.5" strokeDasharray="3 9" />
      </g>
      <g className="cs-spin-rev" style={{ transformOrigin: "200px 200px" }}>
        <ellipse cx="200" cy="200" rx="176" ry="150" fill="none" stroke="#93C5FD" strokeOpacity="0.35" strokeWidth="1.5" strokeDasharray="2 12" />
        {/* orbiting satellite dot sits on the ring's edge */}
        <circle cx="200" cy="50" r="6" fill="#FFFFFF" />
        <circle cx="200" cy="50" r="11" fill="#FFFFFF" opacity="0.25" />
      </g>

      {/* globe body */}
      <circle cx="200" cy="200" r="128" fill="url(#csGlobeFill)" />

      {/* meridians / latitudes */}
      <g stroke="#DBEAFE" strokeOpacity="0.45" strokeWidth="1.2" fill="none">
        <ellipse cx="200" cy="200" rx="128" ry="128" />
        <ellipse cx="200" cy="200" rx="42" ry="128" />
        <ellipse cx="200" cy="200" rx="88" ry="128" />
        <ellipse cx="200" cy="200" rx="128" ry="46" />
        <ellipse cx="200" cy="200" rx="128" ry="90" />
        <line x1="72" y1="200" x2="328" y2="200" />
      </g>

      {/* rotating highlight sweep clipped to the globe */}
      <clipPath id="csClip"><circle cx="200" cy="200" r="128" /></clipPath>
      <g clipPath="url(#csClip)">
        <ellipse className="cs-sweep" cx="150" cy="150" rx="70" ry="120" fill="#FFFFFF" opacity="0.10" style={{ transformOrigin: "200px 200px" }} />
      </g>

      {/* network arc between two pins */}
      <path className="cs-dash" d="M150 158 Q 235 120 262 210" fill="none" stroke="#FFFFFF" strokeOpacity="0.75" strokeWidth="1.6" strokeDasharray="4 5" />

      {/* pulsing location pins */}
      {[
        { x: 150, y: 158 },
        { x: 262, y: 210 },
        { x: 196, y: 262 },
      ].map((p, i) => (
        <g key={i}>
          <circle className="cs-pulse" style={{ transformOrigin: `${p.x}px ${p.y}px`, animationDelay: `${i * 0.7}s` }} cx={p.x} cy={p.y} r="6" fill="#FFFFFF" opacity="0.5" />
          <circle cx={p.x} cy={p.y} r="3.5" fill="#FFFFFF" />
        </g>
      ))}
    </svg>
  );
}

function Countrysl() {
  const navigate = useNavigate();
  const { refreshAuth } = useAuth();
  const { t } = useTranslation();
  const [selectedCountry, setSelectedCountry] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [isOpen, setIsOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  // Generate country list
  const regionNames = new Intl.DisplayNames(['en'], { type: 'region' });
  const countries = getCountries().map((country) => ({
    code: country,
    name: regionNames.of(country) || country,
    flag: country.toUpperCase().replace(/./g, (char) => String.fromCodePoint(127397 + char.charCodeAt(0)))
  })).sort((a, b) => a.name.localeCompare(b.name));

  const handleContinue = async () => {
    if (!selectedCountry) {
      setError(t('country.selectOne'));
      return;
    }

    setLoading(true);
    setError("");

    try {
      // Get user from localStorage
      const userStr = localStorage.getItem("user");
      let token = localStorage.getItem("token") || localStorage.getItem("idToken");

      // Fallback: try to get token from authTokens
      if (!token) {
        const authTokensStr = localStorage.getItem("authTokens");
        if (authTokensStr) {
          const authTokens = JSON.parse(authTokensStr);
          token = authTokens.idToken;
        }
      }

      if (!userStr || !token) {
        console.error("❌ Authentication missing in Countrysl");
        setError("Session expired. Redirecting to signup...");
        setTimeout(() => {
          navigate("/signup", { replace: true });
        }, 2000);
        return;
      }

      const user = JSON.parse(userStr);

      // Decode token to detect UID inside token (to avoid UID mismatch)
      const parseJwt = (tokenStr: string) => {
        try {
          const payload = tokenStr.split('.')[1];
          const decoded = JSON.parse(atob(payload.replace(/-/g, '+').replace(/_/g, '/')));
          return decoded;
        } catch (e) {
          return null;
        }
      };

      let targetUid = user.uid;
      if (token) {
        const decoded = parseJwt(token);
        const tokenUid = decoded?.user_id || decoded?.sub || decoded?.uid;
        if (tokenUid && tokenUid !== user.uid) {
          // Use token's UID for the update and sync localStorage
          targetUid = tokenUid;
          const updatedUser = { ...user, uid: tokenUid };
          localStorage.setItem('user', JSON.stringify(updatedUser));
        }
      }

      // Update user profile with selected country (with retry on invalid token)
      const doUpdate = async (attempt = 1) => {
        const url = `${API_BASE_URL}/auth/user/${targetUid}`;

        const response = await fetch(url, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          // Only consulting/investor users reach country selection, so this also marks
          // consulting as their activated ORIGIN product (accounting stays deactivated
          // until they activate it).
          body: JSON.stringify({ country: selectedCountry, consultingActivated: true })
        });

        const data = await response.json().catch(() => null);

        // If token invalid or unauthorized, try to refresh once
        if ((response.status === 401 || response.status === 403) && attempt === 1) {
          const refreshed = await refreshAuth();
          if (refreshed) {
            // reload token from storage
            token = localStorage.getItem('token') || localStorage.getItem('idToken') || (localStorage.getItem('authTokens') ? JSON.parse(localStorage.getItem('authTokens') as string).idToken : null);
            // Retry once
            return doUpdate(2);
          }
        }

        // If still not ok, provide helpful guidance and surface server error
        if (!response.ok || !data?.success) {
          const errMsg = data?.error || `Failed to save country (status ${response.status})`;
          // If second attempt failed, guide user to re-login
          if (attempt >= 2) {
            console.error('❌ Save country error after retry:', errMsg);
            // Clear maybe-stale auth and redirect to login
            localStorage.removeItem('token');
            localStorage.removeItem('idToken');
            localStorage.removeItem('authTokens');
            localStorage.removeItem('user');
            showAlert('Your session has expired or is invalid. Please log in again.');
            navigate('/login');
            throw new Error(errMsg);
          }

          throw new Error(errMsg);
        }

        return data;
      };

      await doUpdate();

      // Update localStorage with country info
      const updatedUser = { ...user, country: selectedCountry };
      localStorage.setItem("user", JSON.stringify(updatedUser));
      navigate("/readiness-journey");

    } catch (err: any) {
      console.error("❌ Save country error:", err);
      setError(err.message || "Failed to save country. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleBack = () => {
    navigate(-1);
  };

  const selectedFlag = countries.find(c => c.name === selectedCountry);

  return (
    <div className="min-h-screen flex flex-col bg-white">
      {/* Scoped animations for the globe illustration */}
      <style>{`
        @keyframes csSpin { to { transform: rotate(360deg); } }
        @keyframes csSpinRev { to { transform: rotate(-360deg); } }
        @keyframes csPulse {
          0% { transform: scale(1); opacity: .55; }
          70% { transform: scale(2.6); opacity: 0; }
          100% { transform: scale(2.6); opacity: 0; }
        }
        @keyframes csDash { to { stroke-dashoffset: -90; } }
        @keyframes csSweep { to { transform: rotate(360deg); } }
        @keyframes csFloat {
          0%,100% { transform: translateY(0); }
          50% { transform: translateY(-10px); }
        }
        @keyframes csRise {
          from { opacity: 0; transform: translateY(14px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .cs-spin { animation: csSpin 48s linear infinite; }
        .cs-spin-rev { animation: csSpinRev 32s linear infinite; }
        .cs-pulse { animation: csPulse 2.8s ease-out infinite; }
        .cs-dash { animation: csDash 3.5s linear infinite; }
        .cs-sweep { animation: csSweep 18s linear infinite; }
        .cs-chip { animation: csFloat 6s ease-in-out infinite; }
        .cs-chip-a { animation-delay: 0s; }
        .cs-chip-b { animation-delay: .8s; }
        .cs-chip-c { animation-delay: 1.6s; }
        .cs-chip-d { animation-delay: 2.4s; }
        .cs-chip-e { animation-delay: 3.2s; }
        .cs-rise { animation: csRise .6s cubic-bezier(.2,.7,.3,1) both; }
        @media (prefers-reduced-motion: reduce) {
          .cs-spin, .cs-spin-rev, .cs-pulse, .cs-dash, .cs-sweep, .cs-chip, .cs-rise { animation: none !important; }
        }
      `}</style>

      <Navbar hideSignBtn={true}></Navbar>

      <div className="flex flex-1 min-h-0">
        {/* ── Left: animated globe panel ── */}
        <div className="relative hidden lg:flex w-[46%] items-center justify-center overflow-hidden bg-gradient-to-br from-[#0B4CA0] via-[#1565C0] to-[#2E86DE]">
          {/* faint grid texture */}
          <div
            className="absolute inset-0 opacity-[0.12]"
            style={{ backgroundImage: "linear-gradient(#fff 1px, transparent 1px), linear-gradient(90deg, #fff 1px, transparent 1px)", backgroundSize: "44px 44px" }}
          />
          <div className="relative z-10 flex flex-col items-center px-12 text-center">
            <div className="relative">
              <AnimatedGlobe />
              {/* floating market chips */}
              {CHIPS.map((chip, i) => {
                const pos = [
                  "top-2 -left-2",
                  "top-1/4 -right-6",
                  "bottom-10 -left-8",
                  "bottom-2 right-2",
                  "top-1/2 -right-10",
                ][i];
                return (
                  <div
                    key={chip.code}
                    className={`cs-chip ${chip.cls} absolute ${pos} flex items-center gap-2 rounded-full bg-white/95 px-3 py-1.5 shadow-lg backdrop-blur`}
                  >
                    <img src={`https://flagcdn.com/w40/${chip.code}.png`} alt="" className="w-5 h-auto rounded-[2px]" />
                    <span className="text-[11px] font-semibold text-slate-700">{chip.label}</span>
                  </div>
                );
              })}
            </div>
            <h2 className="cs-rise mt-10 text-3xl font-bold text-white leading-tight" style={{ animationDelay: ".05s" }}>
              Capital readiness,<br />wherever you build.
            </h2>
            <p className="cs-rise mt-3 max-w-sm text-sm text-blue-100/90" style={{ animationDelay: ".15s" }}>
              Tell us where you're based so we can tailor your readiness journey to your market and investors.
            </p>
          </div>
        </div>

        {/* ── Right: selector card ── */}
        <div className="flex w-full lg:w-[54%] items-center justify-center bg-[#F7F9FC] px-6 py-12">
          <div className="cs-rise w-full max-w-md" style={{ animationDelay: ".1s" }}>
            <div className="rounded-2xl border border-slate-200 bg-white p-8 shadow-[0_10px_40px_-20px_rgba(15,38,64,0.4)]">
              <span className="inline-flex items-center gap-2 rounded-full bg-blue-50 px-3 py-1 text-[11px] font-semibold uppercase tracking-wider text-[#1976d2]">
                <span className="h-1.5 w-1.5 rounded-full bg-[#1976d2]" /> Step 1 · Location
              </span>
              <h1 className="mt-4 text-2xl font-bold text-slate-900">
                {t('country.selectCountry')}
              </h1>
              <p className="mt-1.5 text-sm text-slate-500">
                Where is your company based? This personalises your readiness journey.
              </p>

              {/* Error Message */}
              {error && (
                <div className="mt-5 flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
                  <svg className="mt-0.5 h-4 w-4 shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
                  <span>{error}</span>
                </div>
              )}

              <div className="mt-6 space-y-5">
                {/* Custom Country Dropdown */}
                <div className="relative">
                  <label className="mb-1.5 block text-xs font-semibold text-slate-600">Country</label>
                  <button
                    type="button"
                    onClick={() => setIsOpen(!isOpen)}
                    className={`flex w-full items-center justify-between rounded-xl border bg-white px-4 py-3.5 text-left text-sm text-slate-900 transition focus:outline-none focus:ring-2 focus:ring-blue-500/40 ${isOpen ? "border-blue-400 ring-2 ring-blue-500/20" : "border-slate-300 hover:border-slate-400"}`}
                  >
                    <span className="flex items-center gap-2.5">
                      {selectedCountry ? (
                        <>
                          {selectedFlag && (
                            <img
                              src={`https://flagcdn.com/w40/${selectedFlag.code.toLowerCase()}.png`}
                              alt={selectedFlag.name}
                              className="h-auto w-6 rounded-sm border border-slate-100"
                            />
                          )}
                          <span className="font-medium">{selectedCountry}</span>
                        </>
                      ) : (
                        <span className="text-slate-400">Select a country</span>
                      )}
                    </span>
                    <svg
                      className={`h-5 w-5 text-slate-400 transition-transform ${isOpen ? 'rotate-180' : ''}`}
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </button>

                  {isOpen && (
                    <div className="absolute z-50 mt-2 flex max-h-64 w-full flex-col overflow-hidden rounded-xl border border-slate-200 bg-white shadow-xl">
                      <div className="sticky top-0 z-10 border-b border-slate-100 bg-white p-2">
                        <input
                          type="text"
                          placeholder="Search country..."
                          className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm text-black placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500"
                          value={searchQuery}
                          onChange={(e) => setSearchQuery(e.target.value)}
                          autoFocus
                          onClick={(e) => e.stopPropagation()}
                        />
                      </div>
                      <div className="flex-1 overflow-auto">
                        {countries
                          .filter((c) => c.name.toLowerCase().includes(searchQuery.toLowerCase()))
                          .map(({ code, name }) => (
                            <button
                              key={code}
                              type="button"
                              onClick={() => {
                                setSelectedCountry(name);
                                setIsOpen(false);
                                setSearchQuery("");
                              }}
                              className="flex w-full items-center gap-3 border-b border-slate-50 px-4 py-2.5 text-left text-sm text-slate-700 transition last:border-0 hover:bg-blue-50/60"
                            >
                              <img
                                src={`https://flagcdn.com/w40/${code.toLowerCase()}.png`}
                                alt={name}
                                className="h-auto w-6 shrink-0 rounded-sm border border-slate-100"
                              />
                              <span className="truncate">{name}</span>
                            </button>
                          ))}
                        {countries.filter((c) => c.name.toLowerCase().includes(searchQuery.toLowerCase())).length === 0 && (
                          <div className="px-4 py-3 text-center text-sm text-slate-500">
                            No countries found
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>

                {/* Buttons */}
                <div className="flex gap-3 pt-1">
                  <button
                    type="button"
                    onClick={handleBack}
                    disabled={loading}
                    className="flex-1 rounded-xl border border-slate-300 bg-white px-6 py-3.5 text-sm font-medium text-slate-700 transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    ← {t('signup.back')}
                  </button>
                  <button
                    type="button"
                    onClick={handleContinue}
                    disabled={loading || !selectedCountry}
                    className="flex-[1.4] rounded-xl bg-gradient-to-r from-[#1565C0] to-[#1976D2] px-6 py-3.5 text-sm font-semibold text-white shadow-lg shadow-blue-600/20 transition hover:opacity-95 active:scale-[.99] disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    {loading ? (
                      <span className="inline-flex items-center justify-center gap-2">
                        <span className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
                        {t('goal.saving')}
                      </span>
                    ) : (
                      `${t('country.continue')} →`
                    )}
                  </button>
                </div>
              </div>
            </div>

            <p className="mt-5 text-center text-xs text-slate-400">
              🔒 Your information is private and used only to personalise your experience.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Countrysl;
